// Direction One — The Document. Scoped to trip details / itinerary; do not replace the global theme.

export const getDocumentInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const HOUSE_DOCUMENT = {
  canvas: '#EBE8E1',
  accent: '#8A6A28',
  oxblood: '#4A0E15',
  wine: '#7A1420',
  nav: '#320910',
  navAccent: '#C9A961',
};

export const DOCUMENT = {
  canvas: `var(--dvn-canvas, ${HOUSE_DOCUMENT.canvas})`,
  surface: '#FCFBF8',
  ink: 'var(--dvn-ink, #1A0E12)',
  ink2: '#3D2E32',
  ink3: '#5C4A4E',
  placeholder: '#6F5E62',
  accent: `var(--dvn-accent, ${HOUSE_DOCUMENT.accent})`,
  rule: '#E1DCD3',
  ruleHard: '#CFC8BC',
  cardRule: '#DFDAD1',
  oxblood: `var(--dvn-oxblood, ${HOUSE_DOCUMENT.oxblood})`,
  // Section headings. Lighter than oxblood so they lift off the canvas, and it
  // resolves to a branded workspace's own oxblood when one is set.
  heading: `var(--dvn-oxblood, ${HOUSE_DOCUMENT.wine})`,
  nav: `var(--dvn-nav, ${HOUSE_DOCUMENT.nav})`,
  navAccent: `var(--dvn-nav-accent, ${HOUSE_DOCUMENT.navAccent})`,
  ivory: '#F6EFE4',
  navInk: 'var(--dvn-nav-ink, rgba(246,239,228,.6))',
  navRule: 'rgba(201,169,97,.2)',
  display: '"Playfair Display", Georgia, serif',
  sans: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
};

export const documentTabIndicatorSx = {
  // MUI sx treats height: 1 as 100%; the mock underline is 1px.
  height: '1px',
  maxHeight: '1px',
  backgroundColor: DOCUMENT.accent,
  borderRadius: 0,
  bottom: 0,
  top: 'auto',
};

export const documentTabSx = {
  minHeight: 52,
  minWidth: 'auto',
  px: 0,
  py: 1.75,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.2,
  fontFamily: DOCUMENT.sans,
  color: DOCUMENT.navInk,
  opacity: 1,
  backgroundColor: 'transparent',
  '&.Mui-selected': {
    color: DOCUMENT.accent,
    backgroundColor: 'transparent',
  },
  '&:hover': {
    color: DOCUMENT.ivory,
    backgroundColor: 'transparent',
  },
  '& .MuiTab-iconWrapper': {
    display: 'none',
  },
  '& .MuiTypography-root': {
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    textTransform: 'inherit',
    fontFamily: 'inherit',
    lineHeight: 1.2,
  },
};

export const documentSubTabSx = {
  minHeight: 48,
  minWidth: 'auto',
  px: 0,
  py: 0,
  pb: 1.5,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.2,
  fontFamily: DOCUMENT.sans,
  color: DOCUMENT.ink3,
  opacity: 1,
  backgroundColor: 'transparent',
  '&.Mui-selected': {
    color: DOCUMENT.accent,
    backgroundColor: 'transparent',
  },
  '&:hover': {
    color: DOCUMENT.ink,
    backgroundColor: 'transparent',
  },
  '& .MuiTab-iconWrapper': {
    display: 'none',
  },
};

export const documentPageTabsSx = {
  minHeight: 52,
  '& .MuiTabs-flexContainer': {
    gap: { xs: 2.5, md: 3.5 },
  },
  '& .MuiTab-root': documentTabSx,
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiTabs-indicator': documentTabIndicatorSx,
  '& .MuiTabs-scrollButtons': {
    color: 'rgba(246,239,228,.62)',
    '&.Mui-disabled': { opacity: 0.28 },
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
};

export const documentChromeTabSx = {
  ...documentTabSx,
  minHeight: 48,
  fontSize: '0.875rem',
  letterSpacing: '0.12em',
  py: 1.5,
  color: 'rgba(246,239,228,.62)',
  '&.Mui-selected': {
    color: DOCUMENT.navAccent,
    backgroundColor: 'transparent',
  },
  '&:hover': {
    color: DOCUMENT.ivory,
    backgroundColor: 'transparent',
  },
};

export const documentChromeTabsSx = {
  minHeight: 48,
  '& .MuiTabs-flexContainer': {
    gap: { xs: 2.5, md: 3.75 },
  },
  '& .MuiTab-root': documentChromeTabSx,
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiTabs-indicator': {
    ...documentTabIndicatorSx,
    backgroundColor: DOCUMENT.navAccent,
  },
  '& .MuiTabs-scrollButtons': {
    color: 'rgba(246,239,228,.62)',
    '&.Mui-disabled': { opacity: 0.28 },
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
};

export const documentSubTabsSx = {
  minHeight: 48,
  borderBottom: `1px solid ${DOCUMENT.rule}`,
  '& .MuiTabs-flexContainer': {
    gap: { xs: 2.5, md: 3.25 },
  },
  '& .MuiTab-root': documentSubTabSx,
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiTabs-indicator': documentTabIndicatorSx,
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
};

export const documentChatCountSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 14,
  height: 14,
  ml: 0.75,
  px: 0.4,
  borderRadius: '7px',
  bgcolor: '#8C2B22',
  color: '#fff',
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: '14px',
  verticalAlign: 'middle',
};

export const documentToggleSx = {
  '& .MuiToggleButton-root': {
    flex: 'none',
    py: 0.75,
    px: 0.5,
    border: `1px solid ${DOCUMENT.ruleHard}`,
    color: DOCUMENT.ink3,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontSize: '0.625rem',
    flexDirection: 'column',
    gap: 0.25,
    '&.Mui-selected': {
      bgcolor: DOCUMENT.surface,
      color: DOCUMENT.accent,
      borderColor: DOCUMENT.accent,
      '&:hover': {
        bgcolor: DOCUMENT.surface,
      },
    },
  },
};

export const documentAddButtonSx = {
  mt: 2.25,
  background: 'none',
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  fontSize: '0.75rem',
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  px: 2,
  py: 1.1,
  color: DOCUMENT.ink2,
  minHeight: 40,
  boxShadow: 'none',
  '&:hover': {
    background: 'none',
    borderColor: DOCUMENT.accent,
    color: DOCUMENT.ink,
    boxShadow: 'none',
    transform: 'none',
  },
};

export const documentOutlineButtonSx = {
  ...documentAddButtonSx,
  mt: 0,
};

export const documentOutlineIconButtonSx = {
  ...documentOutlineButtonSx,
  minWidth: 40,
  px: 1,
};

export const DOCUMENT_NAV_LOGO_HEIGHT = 88;
export const DOCUMENT_NAV_LOGO_HEIGHT_MOBILE = 52;
export const DOCUMENT_NAV_ACTION_SPACING = 1;

const documentNavAuthLabelSx = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.13em',
  lineHeight: 1.2,
  textTransform: 'uppercase',
  fontFamily: DOCUMENT.sans,
};

export const documentNavAuthLinkSx = {
  ...documentNavAuthLabelSx,
  display: 'inline-flex',
  alignItems: 'center',
  background: 'none',
  color: 'rgba(246,239,228,.62)',
  px: 1.25,
  py: 1,
  minHeight: 0,
  minWidth: 0,
  border: 0,
  borderRadius: 0,
  boxShadow: 'none',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    background: 'none',
    color: DOCUMENT.ivory,
    boxShadow: 'none',
  },
};

export const documentNavAuthCtaSx = {
  ...documentNavAuthLabelSx,
  color: DOCUMENT.nav,
  backgroundColor: DOCUMENT.navAccent,
  px: 1.9,
  py: 1.1,
  minHeight: 0,
  minWidth: 0,
  borderRadius: 0,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#DCC183',
    boxShadow: 'none',
  },
};

export const documentNavAuthOutlineSx = {
  ...documentNavAuthLabelSx,
  color: DOCUMENT.ivory,
  backgroundColor: 'transparent',
  border: '1px solid rgba(246,239,228,.45)',
  px: 1.9,
  py: 1.1,
  minHeight: 0,
  minWidth: 0,
  borderRadius: 0,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'rgba(246,239,228,0.08)',
    borderColor: DOCUMENT.navAccent,
    boxShadow: 'none',
  },
};

export const documentNavLogoSx = {
  height: DOCUMENT_NAV_LOGO_HEIGHT,
  width: 'auto',
  maxWidth: 'none',
  objectFit: 'contain',
  objectPosition: 'left center',
  display: 'block',
  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))',
};

/**
 * Section headings on the canvas. Wine separates them from body copy by hue
 * rather than by weight and still clears 8:1 contrast against the canvas, well
 * past AAA. The gold accent is too light for headings at these sizes.
 */
export const documentSectionTitleSx = {
  fontFamily: DOCUMENT.display,
  fontSize: 23,
  fontWeight: 400,
  color: DOCUMENT.heading,
};

/** Same treatment for the smaller headings further down a page. */
export const documentSubSectionTitleSx = {
  ...documentSectionTitleSx,
  fontSize: 21,
};

/**
 * Labels above form fields (create trip, package editor, activity modal, etc.).
 * Same wine + Playfair treatment as dashboard section headers, sized for inputs.
 */
export const documentFormLabelSx = {
  display: 'block',
  fontFamily: DOCUMENT.display,
  fontSize: 18,
  fontWeight: 400,
  lineHeight: 1.25,
  color: DOCUMENT.heading,
  mb: 1,
};

export const documentEyebrowSx = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: DOCUMENT.accent,
};

export const documentPageCanvasSx = {
  bgcolor: DOCUMENT.canvas,
  minHeight: '100vh',
  color: DOCUMENT.ink,
  pb: 'var(--app-footer-pad, 0px)',
};

/** App pages (Memories, My Trips, Packages) share this width. Do not use sm/md for new pages. */
export const PAGE_CONTAINER_MAX_WIDTH = 'xl';

export const documentPageContainerSx = {
  px: { xs: 2, sm: 3, md: 5 },
};

/**
 * Full-bleed shell for Box layouts (partner chrome, list pages).
 * Matches MUI Container maxWidth="xl" used on consumer pages.
 */
export const pageShellSx = {
  width: '100%',
  maxWidth: (theme) => theme.breakpoints.values[PAGE_CONTAINER_MAX_WIDTH],
  mx: 'auto',
  boxSizing: 'border-box',
  ...documentPageContainerSx,
};

export const documentLegalPaperSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  p: { xs: 3, md: 5 },
};

export const documentAuthPaperSx = {
  ...documentLegalPaperSx,
  p: { xs: 3, md: 4.5 },
};

export const documentAuthHeadingSx = {
  fontFamily: DOCUMENT.display,
  fontWeight: 400,
  fontSize: { xs: 26, md: 30 },
  lineHeight: 1.15,
  color: DOCUMENT.ink,
};

export const documentAuthLogoSx = {
  width: 40,
  height: 40,
  mr: 1.5,
  display: 'block',
  objectFit: 'cover',
  borderRadius: 0,
  border: `1px solid ${DOCUMENT.ruleHard}`,
};

export const documentAuthFieldBaseSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.surface,
    fontFamily: DOCUMENT.sans,
    color: DOCUMENT.ink,
    '& fieldset': { borderColor: DOCUMENT.ruleHard },
    '&:hover fieldset': { borderColor: DOCUMENT.accent },
    '&.Mui-focused fieldset': { borderColor: DOCUMENT.accent },
  },
  '& .MuiInputLabel-root': {
    fontFamily: DOCUMENT.sans,
    color: DOCUMENT.ink3,
    '&.Mui-focused': { color: DOCUMENT.accent },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: DOCUMENT.sans,
  },
};

export const documentAuthFieldSx = {
  mt: 2,
  ...documentAuthFieldBaseSx,
};

export const documentAuthButtonSx = {
  mt: 3,
  mb: 3,
  width: '100%',
  py: 1.75,
  minHeight: 48,
  borderRadius: 0,
  border: 0,
  boxShadow: 'none',
  textTransform: 'uppercase',
  letterSpacing: '0.11em',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: DOCUMENT.sans,
  backgroundColor: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  '&:hover': {
    backgroundColor: DOCUMENT.nav,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: DOCUMENT.ruleHard,
    color: DOCUMENT.ink3,
  },
};

export const documentAuthBrandedButtonSx = (brandColor) => ({
  ...documentAuthButtonSx,
  ...(brandColor
    ? {
        backgroundColor: brandColor,
        color: DOCUMENT.nav,
        '&:hover': {
          backgroundColor: brandColor,
          boxShadow: 'none',
        },
      }
    : {}),
});

export const documentAuthLinkSx = {
  color: DOCUMENT.oxblood,
  textDecoration: 'none',
  fontFamily: DOCUMENT.sans,
  fontSize: 13.5,
  fontWeight: 500,
  '&:hover': {
    color: DOCUMENT.nav,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
};

export const documentAuthAlertSx = {
  mb: 3,
  borderRadius: 0,
  border: `1px solid ${DOCUMENT.ruleHard}`,
};

export const documentHeroSearchSx = {
  bgcolor: DOCUMENT.surface,
  borderRadius: 0,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.surface,
    fontFamily: DOCUMENT.sans,
    color: DOCUMENT.ink,
    '& fieldset': { borderColor: DOCUMENT.ruleHard },
    '&:hover fieldset': { borderColor: DOCUMENT.accent },
    '&.Mui-focused fieldset': { borderColor: DOCUMENT.accent },
  },
  '& .MuiInput-root': {
    fontFamily: DOCUMENT.sans,
    color: DOCUMENT.ink,
  },
};

/** Square document Select / FormControl — use with documentSelectMenuProps. */
export const documentSelectSx = {
  ...documentHeroSearchSx,
  '& .MuiInputLabel-root': {
    fontFamily: DOCUMENT.sans,
    fontSize: 12,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: DOCUMENT.ink3,
    '&.Mui-focused': { color: DOCUMENT.oxblood },
  },
  '& .MuiSelect-select': {
    fontFamily: DOCUMENT.sans,
    fontSize: 13,
    color: DOCUMENT.ink2,
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: DOCUMENT.oxblood,
    borderWidth: 1,
  },
};

/** Menu paper for document Selects (square, rule border, capped height). */
export const documentSelectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 240,
      borderRadius: 0,
      border: `1px solid ${DOCUMENT.rule}`,
      backgroundColor: DOCUMENT.surface,
      '& .MuiMenuItem-root': {
        fontFamily: DOCUMENT.sans,
        fontSize: 13,
        color: DOCUMENT.ink,
        '&.Mui-selected': {
          backgroundColor: DOCUMENT.ivory,
          color: DOCUMENT.oxblood,
        },
        '&.Mui-selected:hover, &:hover': {
          backgroundColor: DOCUMENT.ivory,
        },
      },
    },
  },
};

export const documentFilterChipSx = (selected) => ({
  height: 32,
  borderRadius: 0,
  fontFamily: DOCUMENT.sans,
  fontSize: 10.5,
  fontWeight: 500,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: selected ? DOCUMENT.ivory : DOCUMENT.ink2,
  bgcolor: selected ? DOCUMENT.oxblood : 'transparent',
  border: `1px solid ${selected ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
  '&:hover': {
    bgcolor: selected ? DOCUMENT.nav : `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, transparent)`,
  },
});

export const documentToggleBtnSx = (selected) => ({
  borderRadius: 0,
  textTransform: 'uppercase',
  letterSpacing: '0.11em',
  fontSize: 11,
  fontWeight: 500,
  px: 2.5,
  py: 1.1,
  minWidth: 0,
  minHeight: 36,
  boxShadow: 'none',
  color: selected ? DOCUMENT.ivory : DOCUMENT.ink2,
  backgroundColor: selected ? DOCUMENT.oxblood : 'transparent',
  border: `1px solid ${selected ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
  '&:hover': {
    backgroundColor: selected ? DOCUMENT.nav : `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, transparent)`,
    borderColor: selected ? DOCUMENT.nav : DOCUMENT.oxblood,
    boxShadow: 'none',
    color: selected ? DOCUMENT.ivory : DOCUMENT.ink,
  },
});

// Muted, theme-consistent pill used for suggestion category/status badges.
export const documentBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  height: 22,
  px: 1,
  borderRadius: '999px',
  border: `1px solid ${DOCUMENT.ruleHard}`,
  bgcolor: 'transparent',
  color: DOCUMENT.ink2,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1,
  letterSpacing: '0.04em',
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
  '& .MuiSvgIcon-root': {
    fontSize: 13,
    color: DOCUMENT.ink3,
  },
};

const DOCUMENT_STATUS_TONES = {
  approved: { color: '#3F6B3F', borderColor: 'rgba(63,107,63,.42)', bgcolor: 'rgba(63,107,63,.09)' },
  rejected: { color: '#8C2B22', borderColor: 'rgba(140,43,34,.42)', bgcolor: 'rgba(140,43,34,.08)' },
  pending: { color: DOCUMENT.ink3, borderColor: DOCUMENT.ruleHard, bgcolor: 'transparent' },
};

export const getDocumentStatusBadgeSx = (status) => ({
  ...documentBadgeSx,
  textTransform: 'uppercase',
  letterSpacing: '0.09em',
  fontSize: 10,
  ...(DOCUMENT_STATUS_TONES[status] || DOCUMENT_STATUS_TONES.pending),
});

export const documentMapBoxSx = {
  backgroundColor: '#EDEBE6',
  border: `1px solid ${DOCUMENT.ruleHard}`,
  width: '100%',
  minHeight: 420,
  position: 'relative',
  borderRadius: 0,
  overflow: 'hidden',
};

export const documentRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.75,
  py: 1.75,
  borderBottom: `1px solid ${DOCUMENT.rule}`,
};

export const documentTextButtonSx = {
  background: 'none',
  border: 0,
  p: '2px 0',
  minWidth: 0,
  fontSize: '0.75rem',
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  borderRadius: 0,
  boxShadow: 'none',
  '&:hover': {
    background: 'none',
    color: DOCUMENT.ink,
    borderBottom: `1px solid ${DOCUMENT.accent}`,
    boxShadow: 'none',
    transform: 'none',
  },
};

export const documentSolidButtonSx = {
  backgroundColor: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  border: 0,
  borderRadius: 0,
  boxShadow: 'none',
  fontFamily: DOCUMENT.sans,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  px: 2.5,
  py: 1.5,
  minHeight: 40,
  '&:hover': {
    backgroundColor: DOCUMENT.nav,
    boxShadow: 'none',
    transform: 'none',
  },
};

export const documentSolidButtonSmallSx = {
  ...documentSolidButtonSx,
  px: 2,
  py: 1.25,
  minHeight: 36,
};
